import type { QuantityFlagItem, QuantityFlagMetadata } from "../PointMetadata";
import { QUANTITY_FLAG_METADATA } from "../PointMetadata";
import { color } from "@ui";
import t from "../../../../i18n";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { isActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { ChartType } from "../../useNoDataIntervals";
import type { Analysis } from "../../../../api/Analysis";
import type { Setup } from "../../../../api/useSetups";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { Quantity } from "../../../../api/useQuantities";
import { computeQuantityGap } from "diagnostics-domain/quantity/gap";
import { isAnalysisQuantityValid } from "../../metrology";
export const QUANTITY_FLAG_SERIE_ID = "QUANTITY_FLAG_SERIE_ID";
export const FLAG_AXIS = {
  id: "flagAxis",
  visible: false,
  zoomEnabled: false,
  max: 1,
};

function getNoModelOutputDescription(chartType: ChartType) {
  switch (chartType) {
    case "measures":
    case "amplitude":
      return t("measures.chart.tooltip.description.missingValue");
    case "gap":
    case "normalizedGap":
      return t("measures.chart.tooltip.description.noModelOutput");
    default:
      throw new Error(`chartType "${chartType}" is not supported`);
  }
}

const SYMBOL_SVG_NO_VALUE = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9Z" fill="${color.raw.carbon400}"/>
<path d="M9 8.1516L11.97 5.1816L12.8184 6.03L9.8484 9L12.8184 11.97L11.97 12.8184L9 9.8484L6.03 12.8184L5.1816 11.97L8.1516 9L5.1816 6.03L6.03 5.1816L9 8.1516Z" fill="${color.raw.hydrogen}"/>
</svg>
`;

const SYMBOL_SVG_INVALID = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9Z" fill="${color.raw.oxygen300}"/>
<path d="M9 8.1516L11.97 5.1816L12.8184 6.03L9.8484 9L12.8184 11.97L11.97 12.8184L9 9.8484L6.03 12.8184L5.1816 11.97L8.1516 9L5.1816 6.03L6.03 5.1816L9 8.1516Z" fill="${color.raw.hydrogen}"/>
</svg>
`;

const SYMBOL_SVG_NO_VALUE_INVALID = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9Z" fill="url(#paint0_linear_6_30)"/>
<path d="M9 8.1516L11.97 5.1816L12.8184 6.03L9.8484 9L12.8184 11.97L11.97 12.8184L9 9.8484L6.03 12.8184L5.1816 11.97L8.1516 9L5.1816 6.03L6.03 5.1816L9 8.1516Z" fill="white"/>
<defs>
<linearGradient id="paint0_linear_6_30" x1="0" y1="9" x2="18" y2="9" gradientUnits="userSpaceOnUse">
<stop stop-color="${color.raw.carbon400}"/>
<stop offset="0.5" stop-color="${color.raw.carbon400}"/>
<stop offset="0.5" stop-color="${color.raw.oxygen300}"/>
<stop offset="1" stop-color="${color.raw.oxygen300}"/>
</linearGradient>
</defs>
</svg>
`;

export const MARKER_NO_VALUE = `data:image/svg+xml;base64,${window.btoa(
  unescape(encodeURIComponent(SYMBOL_SVG_NO_VALUE)),
)}`;
export const MARKER_SVG_INVALID = `data:image/svg+xml;base64,${window.btoa(
  unescape(encodeURIComponent(SYMBOL_SVG_INVALID)),
)}`;
export const MARKER_SVG_NO_VALUE_INVALID = `data:image/svg+xml;base64,${window.btoa(
  unescape(encodeURIComponent(SYMBOL_SVG_NO_VALUE_INVALID)),
)}`;

export function getPoints(
  analyses: (Analysis | ActiveAnalysis)[],
  quantities: Quantity[],
  withSymptomFlags: boolean,
  chartType: ChartType,
  setups: Setup[],
  _metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
) {
  return analyses.reduce<Highcharts.PointOptionsObject[]>((pointArray, analysis) => {
    const quantityFlags = quantities.reduce<QuantityFlagItem[]>((res, quantity) => {
      const quantityValue = analysis.quantityValues[quantity.id];

      if (quantityValue) {
        const analysisSetup = getAnalysisSetup(setups, analysis);
        const offset = getQuantityOffset(analysisSetup, quantity.id);
        const sigma = getQuantitySigma(analysisSetup, quantity.id);
        const quantityValidity =
          isActiveAnalysis(analysis) && "simulated" in quantityValue
            ? isAnalysisQuantityValid(analysis, quantity)
            : true;
        const isInvalidQuantity = !quantityValidity;

        if (chartType === "std" && quantityValue.std === null) {
          res.push({
            name: quantity.name,
            type: "missingValue",
            description: t("measures.chart.tooltip.description.missingValue"),
            origin: "measured",
          });
        } else if (
          withSymptomFlags &&
          ((chartType === "normalizedGap" && sigma === null) ||
            ((chartType === "gap" || chartType === "normalizedGap") && isInvalidQuantity))
        ) {
          res.push({
            name: quantity.name,
            type: "noModelOutput",
            description: getNoModelOutputDescription(chartType),
            origin: "measured",
          });
        } else if (
          withSymptomFlags &&
          (!("simulated" in quantityValue) ||
            computeQuantityGap(quantityValue.measured, quantityValue.simulated, offset) === null)
        ) {
          res.push({
            name: quantity.name,
            type: "noModelOutput",
            description: getNoModelOutputDescription(chartType),
            origin: "expected",
          });
        }

        if (!displayInvalidPoints) {
          if (isInvalidQuantity && chartType !== "gap" && chartType !== "normalizedGap") {
            res.push({
              name: quantity.name,
              type: "invalidValue",
              description: t("measures.chart.tooltip.description.invalid"),
              origin: "measured",
            });
          }
        }
      } else {
        res.push({
          name: quantity.name,
          type: "missingValue",
          description: t("measures.chart.tooltip.description.missingValue"),
          origin: "measured",
        });
      }
      return res;
    }, []);

    if (quantityFlags.length < 1) return pointArray;

    const custom: QuantityFlagMetadata = {
      type: QUANTITY_FLAG_METADATA,
      group: analysis.group,
      label: analysis.label,
      flags: quantityFlags,
      human: null,
    };

    const isInvalidQuantity = quantityFlags.map(m => m.type).some(s => s === "invalidValue");
    const isNoValue = quantityFlags
      .map(m => m.type)
      .some(s => s === "missingValue" || s === "noModelOutput");

    pointArray.push({
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      custom,
      y: 0,
      marker: {
        symbol: `url(${
          isNoValue && isInvalidQuantity
            ? MARKER_SVG_NO_VALUE_INVALID
            : isNoValue
              ? MARKER_NO_VALUE
              : MARKER_SVG_INVALID
        })`,
        radius: 5,
        lineColor: color.hydrogen,
      },
    });

    return pointArray;
  }, []);
}

function getQuantityFlagSerie({
  analyses,
  quantities,
  withSymptomFlags,
  chartType,
  setups,
  metrologyOverrides,
  displayInvalidPoints,
}: {
  analyses: Analysis[] | ActiveAnalysis[];
  quantities: Quantity[];
  withSymptomFlags: boolean;
  chartType: ChartType;
  setups: Setup[];
  metrologyOverrides: MetrologyOverrides;
  displayInvalidPoints: boolean;
}): Highcharts.SeriesScatterOptions {
  return {
    id: QUANTITY_FLAG_SERIE_ID,
    name: "",
    data: getPoints(
      analyses,
      quantities,
      withSymptomFlags,
      chartType,
      setups,
      metrologyOverrides,
      displayInvalidPoints,
    ),
    yAxis: FLAG_AXIS.id,
    replicateInNavigator: false,
    type: "scatter",
    color: "transparent", // hide hallow effect on point hover
    zIndex: zIndexChart.flagSeries,
  };
}

export default getQuantityFlagSerie;
